/**
 * QuickReadButton Component
 *
 * A prominent call-to-action button that starts the QuickRead experience.
 * Uses the recommendation system to find the best next story for the student.
 */

import { Button, Loader, Tooltip } from '@mantine/core';
import { IconPlayerPlay, IconSparkles } from '@tabler/icons-react';
import { useQuickRead } from '../hooks/useQuickRead';

interface QuickReadButtonProps {
  /** Button size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Whether to show as full width */
  fullWidth?: boolean;
  /** Button variant */
  variant?: 'filled' | 'light' | 'outline' | 'gradient';
  /** Custom class name */
  className?: string;
}

export function QuickReadButton({
  size = 'md',
  fullWidth = false,
  variant = 'gradient',
  className,
}: QuickReadButtonProps) {
  const { startQuickRead, state, isDisabled } = useQuickRead();

  const buttonContent = () => {
    switch (state) {
      case 'loading':
        return (
          <>
            <Loader size={16} color="white" />
            Finding your story...
          </>
        );
      case 'generating':
        return (
          <>
            <Loader size={16} color="white" />
            Creating story...
          </>
        );
      default:
        return (
          <>
            <IconSparkles size={18} />
            Quick Read
          </>
        );
    }
  };

  return (
    <Tooltip
      label="Start reading the best story for you right now"
      position="bottom"
      withArrow
      disabled={isDisabled}
    >
      <Button
        onClick={startQuickRead}
        disabled={isDisabled}
        size={size}
        fullWidth={fullWidth}
        variant={variant}
        gradient={variant === 'gradient' ? { from: 'teal', to: 'cyan', deg: 90 } : undefined}
        leftSection={state === 'idle' ? <IconPlayerPlay size={18} /> : null}
        className={className}
        styles={{
          root: {
            fontWeight: 600,
          },
        }}
      >
        {buttonContent()}
      </Button>
    </Tooltip>
  );
}
